// react
import {
	ChangeEvent,
	Dispatch,
	MouseEvent,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react';

// types
import { AccountPayload, ErrorCause, ExpenseType } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, Grid, SelectChangeEvent } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';

// ui
import { Loading } from '../../ui/Loading/Loading';

// props
export interface FormEditExpenseTypeProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormEditExpenseType = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormEditExpenseTypeProps) => {
	// hooks
	const { t } = useTranslation();

	// state
	const [error, setError] = useState<string | null>(null);
	const [expenseType, setExpenseType] = useState<ExpenseType | null>(null);
	const [searchParams] = useSearchParams();
	const [values, setValues] = useState({
		name: '',
	});

	// params
	const paramExpenseType = searchParams.get('dialogExpenseType');

	// context
	const { setBanner } = useContext(ContextBanner);

	// vars
	const idForm = 'form-edit-expense-type';

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// fetch expense type
	const { isLoading: isLoadingExpenseType } = useFetch({
		url: `${process.env.REACT_APP_API_URL}/expense-types/${paramExpenseType}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data && res.data.length) {
				setExpenseType(res.data[0]);
			}
		},
	});

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear error
			setError(null);

			// set loading
			setIsLoading(true);

			if (!paramExpenseType) {
				throw new Error('An ID is required', {
					cause: {
						id: 'ID_NON_EXISTENT',
					},
				});
			}

			const body: AccountPayload = {
				name: String(entries.name),
			};

			const fetchEditExpenseType = await fetch(
				`${process.env.REACT_APP_API_URL}/expense-types/${paramExpenseType}`,
				{
					method: 'PATCH',
					body: JSON.stringify(body),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				}
			);
			const resEditExpenseType = await fetchEditExpenseType.json();

			if (resEditExpenseType.error) {
				throw new Error(resEditExpenseType.error.message, {
					cause: {
						id: resEditExpenseType.error.id,
						details: resEditExpenseType.error?.details,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.expenseTypeUpdated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			let message = t('error.default');

			if (cause) {
				if (cause.id === 'EXPENSE_TYPE_NON_EXISTENT') {
					message = t('error.expenseTypeNonExistent', { id: expenseType?.id });
				}

				if (cause.id === 'ID_NON_EXISTENT') {
					message = t('error.idNonExistent');
				}
			}

			// set error
			setError(message);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	const handleChangeValue = (e: ChangeEvent | SelectChangeEvent<unknown>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues({
			...values,
			[name]: value,
		});
	};

	// content
	const renderContent = () => {
		if (isLoadingExpenseType) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				{error && (
					<Grid item xs={12}>
						<Alert severity="error">{error}</Alert>
					</Grid>
				)}
			</>
		);
	};

	// set default values when expense type changes
	useEffect(() => {
		if (expenseType) {
			setValues({
				name: expenseType.name,
			});
		}
	}, [expenseType]);

	return (
		<form id={`${idForm}`} name="formEditExpenseType" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</form>
	);
};
